import { useState, useEffect, useCallback, useRef } from 'react'
import { apiFetch } from '@/lib/api'
import Sidebar from '@/components/Sidebar'
import ArticleList from '@/components/ArticleList'

interface Article {
  id: number
  title: string
  feed_title: string
  published_at: string
  read: boolean
  url: string
  content: string | null
  author: string | null
  image_url: string | null
  favorited: boolean
}

interface Feed {
  id: number
  title: string
  updated_at: string
  subscription_id: number | null
}

interface PagyMeta {
  page: number
  pages: number
  count: number
}

interface PrefetchedPage {
  page: number
  articles: Article[]
  pagy: PagyMeta
}

interface FeedAppProps {
  feeds: Feed[]
  categories: string[]
  defaultViewMode?: 'compact' | 'expanded'
}

export default function FeedApp({ feeds, categories, defaultViewMode = 'compact' }: FeedAppProps) {
  const [articles, setArticles] = useState<Article[]>([])
  const [pagy, setPagy] = useState<PagyMeta | null>(null)
  const [loading, setLoading] = useState(true)
  const [loadingMore, setLoadingMore] = useState(false)
  const [activeFeedId, setActiveFeedId] = useState<number | null>(null)
  const [activeCategory, setActiveCategory] = useState<string | null>(null)
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [showRead, setShowRead] = useState(false)
  const [sortDirection, setSortDirection] = useState<'desc' | 'asc'>('desc')
  const [viewMode, setViewMode] = useState<'compact' | 'expanded'>(defaultViewMode)
  const prefetchedRef = useRef<PrefetchedPage | null>(null)
  const prefetchingRef = useRef(false)

  function buildParams(page: number): URLSearchParams {
    const params = new URLSearchParams({ page: String(page) })
    if (activeFeedId) params.set('feed_id', String(activeFeedId))
    if (activeCategory) params.set('category', activeCategory)
    if (showRead) params.set('show_read', 'true')
    if (sortDirection === 'asc') params.set('sort', 'asc')
    return params
  }

  function prefetchNextPage(currentPagy: PagyMeta) {
    if (currentPagy.page >= currentPagy.pages || prefetchingRef.current) return
    const nextPage = currentPagy.page + 1
    if (prefetchedRef.current?.page === nextPage) return

    prefetchingRef.current = true
    const params = buildParams(nextPage)
    apiFetch(`/articles.json?${params}`)
      .then((res) => res.json())
      .then((data) => {
        prefetchedRef.current = { page: nextPage, articles: data.articles, pagy: data.pagy }
      })
      .catch(() => {
        // Prefetch is best-effort; ignore errors
      })
      .finally(() => {
        prefetchingRef.current = false
      })
  }

  useEffect(() => {
    prefetchedRef.current = null
    fetchArticles(1, false)
  }, [activeFeedId, activeCategory, showRead, sortDirection])

  async function fetchArticles(page: number, append: boolean) {
    if (append) {
      setLoadingMore(true)
    } else {
      setLoading(true)
    }

    // Use prefetched data if available for this page
    const cached = prefetchedRef.current
    if (cached && cached.page === page) {
      prefetchedRef.current = null
      if (append) {
        setArticles((prev) => [...prev, ...cached.articles])
      } else {
        setArticles(cached.articles)
      }
      setPagy(cached.pagy)
      if (append) {
        setLoadingMore(false)
      } else {
        setLoading(false)
      }
      prefetchNextPage(cached.pagy)
      return
    }

    const params = buildParams(page)
    const res = await apiFetch(`/articles.json?${params}`)
    const data = await res.json()

    if (append) {
      setArticles((prev) => [...prev, ...data.articles])
    } else {
      setArticles(data.articles)
    }
    setPagy(data.pagy)

    if (append) {
      setLoadingMore(false)
    } else {
      setLoading(false)
    }

    prefetchNextPage(data.pagy)
  }

  const loadMore = useCallback(() => {
    if (!pagy || pagy.page >= pagy.pages || loadingMore) return
    fetchArticles(pagy.page + 1, true)
  }, [pagy, loadingMore, activeFeedId, activeCategory, showRead, sortDirection])

  const hasMore = pagy ? pagy.page < pagy.pages : false

  const markArticleRead = useCallback(async (articleId: number) => {
    try {
      await apiFetch(`/articles/${articleId}/article_reads`, { method: 'POST' })
      setArticles((prev) => {
        if (showRead) {
          return prev.map((a) => (a.id === articleId ? { ...a, read: true } : a))
        }
        return prev.filter((a) => a.id !== articleId)
      })
    } catch {
      // Silently fail - user can retry
    }
  }, [showRead])

  function selectFeed(feedId: number | null) {
    setActiveFeedId(feedId)
    setActiveCategory(null)
  }

  function selectCategory(category: string) {
    setActiveCategory(category)
    setActiveFeedId(null)
  }

  return (
    <div id="layout" className={sidebarOpen ? '' : 'sidebar-collapsed'}>
      {sidebarOpen && (
        <Sidebar
          feeds={feeds}
          categories={categories}
          activeFeedId={activeFeedId}
          activeCategory={activeCategory}
          onSelectFeed={selectFeed}
          onSelectCategory={selectCategory}
          onCollapse={() => setSidebarOpen(false)}
        />
      )}

      <main id="article-list">
        <div className="article-list-header">
          {!sidebarOpen && (
            <button className="sidebar-expand-btn" onClick={() => setSidebarOpen(true)} title="Expand sidebar">
              &raquo;
            </button>
          )}

          <label className="show-read-toggle">
            <input
              type="checkbox"
              checked={showRead}
              onChange={(e) => setShowRead(e.target.checked)}
            />
            Show read
          </label>

          <button
            className="sort-toggle-btn"
            onClick={() => setSortDirection(d => d === 'desc' ? 'asc' : 'desc')}
            title={sortDirection === 'desc' ? 'Newest first' : 'Oldest first'}
          >
            Date {sortDirection === 'desc' ? '\u2193' : '\u2191'}
          </button>

          <div className="view-mode-toggle">
            <button
              className={`view-mode-btn ${viewMode === 'compact' ? 'active' : ''}`}
              onClick={() => setViewMode('compact')}
              title="Compact view"
            >
              Compact
            </button>
            <button
              className={`view-mode-btn ${viewMode === 'expanded' ? 'active' : ''}`}
              onClick={() => setViewMode('expanded')}
              title="Expanded view"
            >
              Expanded
            </button>
          </div>
        </div>

        <ArticleList
          articles={articles}
          hasMore={hasMore}
          loading={loading}
          loadingMore={loadingMore}
          onLoadMore={loadMore}
          onMarkRead={markArticleRead}
          feedId={activeFeedId}
          category={activeCategory}
          sortDirection={sortDirection}
          viewMode={viewMode}
        />
      </main>
    </div>
  )
}
